import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { HeroToolbar, type HeroFilterState, type HeroSortState } from '@/components/hero-toolbar';
import { applyHeroSort } from '@/components/hero-filter-bridge';
import { useAuth } from '@/lib/auth-context';
import { FormDrawer } from '@/components/ui/form-drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { PRODUCT_CLASSES, enumToOptions } from '@/lib/status-enums';

/**
 * ProductsPage — canonical list-page reference implementation.
 *
 * Reference: ianfiles/ui-design-system.md §3.2 (rules 1-8).
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │ PageHeader  Title                          [+ New Product] │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ HeroToolbar                                                │
 *   │   [Filter By ▾] [Sort By ▾]              [🔍 Search] [☰] │
 *   ├────────────────────────────────────────────────────────────┤
 *   │ DataTable (tableKey="products-list")                       │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Key rules baked in:
 *   - Page-header right cluster contains ONLY the primary "+ New Product".
 *   - HeroToolbar owns Filter By / Sort By / Search / Columns.
 *   - DataTable shares `tableKey` with HeroToolbar so visibility prefs
 *     persist to localStorage.
 *   - `data-testid="button-new-product"` on the primary action.
 */
const productFormSchema = z.object({
  name: z.string().min(1, 'Product name is required'),
  code: z.string().min(1, 'Product code is required'),
  description: z.string().optional(),
  productClass: z.enum(PRODUCT_CLASSES),
  categoryId: z.string().optional(),
  status: z.enum(['active', 'inactive']),
  basePrice: z.string().optional(),
  baseCost: z.string().optional(),
});

type ProductFormData = z.infer<typeof productFormSchema>;

interface Product {
  id: string;
  name: string;
  code: string;
  sku?: string;
  description: string | null;
  productClass: string;
  categoryId: string | null;
  status: string;
  basePrice: string | null;
  baseCost: string | null;
  createdAt: string;
}

interface Category {
  id: string;
  name: string;
}

interface ProductsResponse {
  products: Product[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function ProductsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<HeroFilterState>({});
  const [sort, setSort] = useState<HeroSortState | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('datatable:products-list:visibility');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<ProductsResponse>({
    queryKey: [
      '/api/products',
      { page: currentPage, limit: pageSize, search, filters },
      sessionId,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(search && { search }),
        ...(filters.status?.length ? { status: filters.status.join(',') } : {}),
        ...(filters.productClass?.length
          ? { productClass: filters.productClass.join(',') }
          : {}),
      });
      return await api.get<ProductsResponse>(`/products?${params}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['/api/products/categories', sessionId],
    queryFn: async () => {
      return await api.get('/products/categories', sessionId);
    },
    enabled: !!sessionId,
  });

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      productClass: 'stock',
      categoryId: '',
      status: 'active',
      basePrice: '',
      baseCost: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      return await api.post('/products', data, sessionId);
    },
    onSuccess: () => {
      toast({
        title: 'Product created',
        description: 'The product has been created successfully.',
      });
      setDialogOpen(false);
      form.reset();
      refetch();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create product',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: ProductFormData) => {
    createMutation.mutate(data);
  };

  // ─── Toolbar columns ─────────────────────────────────────────────────────
  const heroColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
      },
      {
        key: 'code',
        label: 'Code',
        sortable: true,
      },
      {
        key: 'productClass',
        label: 'Type',
        filterable: true,
        sortable: true,
        multi: true,
        options: enumToOptions(PRODUCT_CLASSES),
        clientFilter: false, // server-side via productClass query param
      },
      {
        key: 'status',
        label: 'Status',
        filterable: true,
        multi: true,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' },
        ],
        clientFilter: false, // server-side via status query param
      },
      {
        key: 'createdAt',
        label: 'Created',
        sortable: true,
      },
    ],
    []
  );

  // ─── DataTable columns ───────────────────────────────────────────────────
  const tableColumns = useMemo(
    () => [
      {
        key: 'code',
        header: 'Code',
        cell: (row: Product) => (
          <span className="font-mono text-sm">{row.code}</span>
        ),
      },
      {
        key: 'name',
        header: 'Product Name',
        sortable: true,
        cell: (row: Product) => (
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{row.name}</div>
          </div>
        ),
      },
      {
        key: 'productClass',
        header: 'Type',
        cell: (row: Product) => (
          <StatusBadge status={row.productClass} type="product" />
        ),
      },
      {
        key: 'basePrice',
        header: 'Price',
        hideable: true,
        cell: (row: Product) => (
          <span className="text-sm text-muted-foreground tabular-nums">
            {row.basePrice ? `$${parseFloat(row.basePrice).toFixed(2)}` : '-'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (row: Product) => (
          <StatusBadge status={row.status} type="account" />
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        hideable: true,
        defaultHidden: true,
        cell: (row: Product) => (
          <span className="text-xs text-muted-foreground">
            {new Date(row.createdAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    []
  );

  const products = data?.products || [];
  const total = data?.total || 0;

  const sortedRows = useMemo(
    () => applyHeroSort(products, sort),
    [products, sort]
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Products"
        actions={
          <Button
            size="sm"
            onClick={() => setDialogOpen(true)}
            data-testid="button-new-product"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Product
          </Button>
        }
      />

      <HeroToolbar
        columns={heroColumns}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search products..."
        filters={filters}
        onFiltersChange={setFilters}
        sort={sort}
        onSortChange={setSort}
        tableKey="products-list"
        tableColumns={tableColumns}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      />

      <DataTable
        tableKey="products-list"
        columnVisibility={columnVisibility}
        data={sortedRows}
        columns={tableColumns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/products/${row.id}`)}
        emptyMessage="No products yet — click + New Product to add one"
        emptyIcon={<Package className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={total}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        sortKey={sort?.columnKey ?? null}
        sortDirection={sort?.direction}
        onSort={(key, direction) => setSort({ columnKey: key, direction })}
        data-testid="datatable-products"
      />

      {/* Right-side FormDrawer (canonical add/edit surface — §2.7) */}
      <FormDrawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="New Product"
        description="Add a new product to your catalog."
        formId="create-product"
        isPending={createMutation.isPending}
        submitLabel="Create Product"
        size="2xl"
        data-testid="drawer-new-product"
      >
        <Form {...form}>
          <form id="create-product" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Code *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="PROD-001"
                          variant="flat"
                          data-testid="input-product-code"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Name *</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Product name"
                          variant="flat"
                          data-testid="input-product-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Product description..."
                        rows={3}
                        variant="flat"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="productClass"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Type *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger variant="flat">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="stock">Stock</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="categoryId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger variant="flat">
                            <SelectValue placeholder="Select category (optional)" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="basePrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Price</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          variant="flat"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="baseCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Base Cost</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          variant="flat"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger variant="flat">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
